<script setup lang="ts">
import type { Category, ListingFilter, AggregationFilterEntityOption } from '~/types/shopware'

// Use shared listing types from src/types/shopware to avoid 'any'
import { ComputedRef, UnwrapNestedRefs } from 'vue'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const router = useRouter()

const { t } = useI18n()

const props = defineProps<{
  category: Category
}>()

// Initialize query params - safe for SSR because route is available on both server and client
const order = computed(() =>
  Array.isArray(route.query.order) ? (route.query.order[0] ?? '') : (route.query.order ?? ''),
)
const properties = computed(() =>
  Array.isArray(route.query.properties) ? (route.query.properties[0] ?? '') : (route.query.properties ?? ''),
)

// CARW-1626: when the user lands on a category with no `?properties=` (a "fresh visit"),
// preselect the language property option matching the current locale so the listing
// only shows products in that language. Any present `?properties=` (including empty
// string) means the user has explicit filter state and is respected as-is.
const { idForLocale } = useShopLanguagePropertyOptions()
const { languageCode } = useLocale()
const isFreshVisit = route.query.properties === undefined
const seededProperties = isFreshVisit ? idForLocale(languageCode.value) : properties.value

const {
  getElements,
  search,
  getCurrentPage,
  changeCurrentPage,
  getTotalPagesCount,
  getAvailableFilters,
  getCurrentFilters,
  filtersToQuery,
  getCurrentSortingOrder,
  getSortingOrders,
  changeCurrentSortingOrder,
} = useListing({
  listingType: 'categoryListing',
  categoryId: props.category.id,
  defaultSearchCriteria: {
    // set the default criteria
    search: '', // Required search parameter
    limit: 12,
    p: Number(route?.query?.p) || 1,
    properties: seededProperties,
    order: order.value,
    associations: {
      properties: {},
      seoUrls: {},
    },
  },
})

const sidebarSelectedFilters: UnwrapNestedRefs<{ properties: Set<string> }> = reactive({
  properties: new Set<string>(),
})

const searchCriteriaForRequest: ComputedRef<{
  [code: string]: string | string[] | number | number[] | boolean | undefined
}> = computed(() => ({
  properties: [...sidebarSelectedFilters.properties],
  order: getCurrentSortingOrder.value || '',
}))

const updateFilter = async (filter: { code: string; value: string[] }) => {
  sidebarSelectedFilters[filter.code]?.clear()
  filter.value.forEach((item) => sidebarSelectedFilters[filter.code]?.add(item))
  await search({
    search: '', // Ensure search is always provided
    ...filtersToQuery(searchCriteriaForRequest.value),
    p: 1,
  })
  await router.push({
    query: {
      ...filtersToQuery(searchCriteriaForRequest.value),
      p: 1,
    },
  })
}

const languageFilter = computed(() => {
  const filters = (getAvailableFilters.value ?? []) as ListingFilter[]
  return filters.filter((filter: ListingFilter) => {
    const firstOption = filter.options?.[0] as AggregationFilterEntityOption | undefined
    return (
      firstOption?.translated?.customFields?.custom_property_fields_language_property_group_code === 'language-group'
    )
  })
})

const articleTypeFilterOptions = computed(() => {
  const filters = (getAvailableFilters.value ?? []) as ListingFilter[]
  const typed = filters
    .filter((filter: ListingFilter) => {
      const firstOption = filter.options?.[0] as AggregationFilterEntityOption | undefined
      return (
        (firstOption?.translated?.customFields?.custom_property_fields_type_property_group_code ===
          'product-set-group' ||
          firstOption?.translated?.customFields?.custom_property_fields_type_property_group_code ===
            'product-type-group') &&
        Boolean(filter.options?.length)
      )
    })
    .flatMap((filter) => (filter.options ?? []) as AggregationFilterEntityOption[])
    .sort((a, b) => {
      const aIsSet = a.translated?.customFields?.custom_property_fields_type_property_code === 'set'
      const bIsSet = b.translated?.customFields?.custom_property_fields_type_property_code === 'set'
      if (aIsSet && !bIsSet) return 1
      if (!aIsSet && bIsSet) return -1
      return 0
    })
  return typed
})

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || '',
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
        p: 1,
      },
    })
    changeCurrentSortingOrder(order, { search: '', ...route.query })
  },
})

const sortingOptions = computed(
  (): Array<{ value: string; label: string }> =>
    getSortingOrders.value?.map((sorting) => ({ value: sorting.key, label: sorting.label })) ?? [],
)

const changePage = async (page: number) => {
  await router.push({
    query: {
      ...route.query,
      p: page,
    },
  })
  changeCurrentPage(page, { search: '', ...route.query })
}

// Initialize filters from query params - safe for SSR as route is available on both sides
for (const param in route.query) {
  if (sidebarSelectedFilters.hasOwnProperty(param)) {
    if (sidebarSelectedFilters[param] && typeof sidebarSelectedFilters[param] === 'object') {
      const queryValue = route.query[param]
      if (typeof queryValue === 'string') {
        queryValue.split('|').forEach((element) => {
          sidebarSelectedFilters[param].add(element)
        })
      }
    } else {
      sidebarSelectedFilters[param] = route.query[param]
    }
  }
}

// CARW-1626: seed the language UUID into the reactive filter Set on fresh visits so
// updateFilter() preserves it when the user picks article-type / sort. Without this,
// rebuilding the URL from sidebarSelectedFilters would silently drop the language.
if (isFreshVisit && seededProperties) {
  sidebarSelectedFilters.properties.add(seededProperties)
}

// Perform initial search - this should be awaited at top level for SSR
await search({ search: '' })

// CARW-1626: if the seeded language isn't represented in the actual language facet
// for this category (e.g. EN locale on a category with no EN products), the seed
// would yield an empty listing. Drop it and re-search so the user sees what's
// available; otherwise rewrite the URL so reloads/shares carry the seed explicitly.
if (isFreshVisit && seededProperties) {
  const seededIsAvailable = languageFilter.value.some((filter) => {
    const optionIds = (filter.options ?? []).map((o) => o.id)
    const entityIds = (filter.entities ?? []).map((e) => e.id)
    return optionIds.includes(seededProperties) || entityIds.includes(seededProperties)
  })

  if (!seededIsAvailable) {
    sidebarSelectedFilters.properties.delete(seededProperties)
    // Explicit empty `properties` is required to override the seeded UUID in
    // useListing's searchDefaults — filtersToQuery would omit the key when empty.
    await search({
      search: '',
      ...filtersToQuery(searchCriteriaForRequest.value),
      properties: '',
      p: 1,
    })
  } else if (import.meta.client) {
    router.replace({ query: { ...route.query, properties: seededProperties } })
  }
}
</script>
<template>
  <div>
    <div v-animate="{ animation: 'vertical-reveal' }" class="filters">
      <div class="grid">
        <div class="tl-5 ds-6">
          <ShopSearch />
        </div>
        <div class="tl-7 ds-6 d-5 d-o-1 filters-wrapper">
          <ShopFilterLanguage
            v-if="languageFilter.length"
            :key="languageFilter[0].code"
            :filter="languageFilter[0]"
            :current-filters="getCurrentFilters || { properties: [] }"
            @select-value="updateFilter"
          />
          <Select
            v-model="currentSortingOrder"
            name="sorting"
            :required="false"
            label="Sortierung"
            :options="sortingOptions"
          />
        </div>
      </div>
      <ShopFilterArticleType
        v-if="articleTypeFilterOptions.length"
        :filter-options="articleTypeFilterOptions"
        :current-filters="getCurrentFilters || { properties: [] }"
        @select-value="updateFilter"
      />
    </div>
    <Products v-if="getElements?.length" :products="getElements" />
    <div v-else>
      {{ t('shop.listingPage.noProductsMessage') }}
    </div>
    <Pagination
      v-if="getTotalPagesCount > 1"
      :page="Number(getCurrentPage)"
      :max="getTotalPagesCount"
      @update:page="changePage"
    />
  </div>
</template>
<style scoped lang="scss">
.products {
  margin-bottom: 64px;
}
.filters {
  padding: 24px 0;
  border-top: 1px solid var(--c-line-light);
  border-bottom: 1px solid var(--c-line-light);
  @include fluid(margin-bottom, 32px, 96px);
  .grid {
    row-gap: 16px;
    margin-bottom: 32px;
  }
  .filters-wrapper {
    display: grid;
    grid-template-columns: 100%;
    gap: 16px;
    @include breakpoint(ts) {
      grid-template-columns: 1fr 1fr;
    }
  }
}
</style>
